import { useAppSelector } from "../app/hooks";
import { ChatMessageType, type ChatMessageDto } from "../contracts";
import type { CommercialSimulatorAgentResponse } from "../models/agentResponseModels";
import type { ChatMessageViewModel } from "../models/chatMessageViewModel";
import { getToolRequestMessageById, isCommercialSimulatorSubBrandToolRequest, isCommercialSimulatorToolRequest, type CommercialSimulatorSubBrandToolRequest, type CommercialSimulatorToolRequest } from "../models/toolRequestModels";
import { getToolResponseById, getToolResponseMessageById, isCommercialSimulatorToolResponse, type CommercialSimulatorToolResponse, type ToolResponse } from "../models/toolResponseModels";
import { selectCurrentCommercialPlan, selectMessages } from "../slices/chatSlice";
import { capitalizeFirstLetter } from "../utils/stringValueConversion";
import { getUnitOfMeasureAbbreviation } from "../utils/unitOfMeasureUtils";

type CurrentPlanData = {
    scenarioDefaultName: string | null;
    chatConversationId: number | null;
    chatConversationMessageId: number | null;
    currentPlan: CommercialSimulatorAgentResponse | null;
    currentPlanData: SimulationPlanData | null;
    hasPlan: boolean;
    headingData: SimulationHeadingData | null;
    toolRequest: ChatMessageViewModel | null;
    toolResponse: ChatMessageViewModel | null;
};

export type SimulationHeadingData = {
    objective: string;
    skuName?: string;
    subBrandName?: string;
    retailers: string;
};

export type SimulationPlanData = {
    planningLevel: string;
    budget: string;
};

const emptyCurrentPlanData: CurrentPlanData = {
    scenarioDefaultName: null,
    chatConversationId: null,
    chatConversationMessageId: null,
    currentPlan: null,
    currentPlanData: null,
    hasPlan: false,
    headingData: null,
    toolRequest: null,
    toolResponse: null
};

type RetailerInput = {
    retailer_name?: string | null;
};

const getRetailerNames = (
    retailers: readonly RetailerInput[] | null | undefined
): string | null => {
    const retailerNames = retailers
        ?.filter(
            (retailer): retailer is RetailerInput & { retailer_name: string } =>
                Boolean(retailer.retailer_name)
        )
        .map(retailer => capitalizeFirstLetter(retailer.retailer_name))
        .join(", ");

    return retailerNames || null;
};

const getCommercialSimulationHeadingData = (
    chatMessages: ChatMessageDto[],
    toolRequest: CommercialSimulatorToolRequest
): SimulationHeadingData | null => {
    const input = toolRequest.input;

    if (!input?.objective || !input.sku_name) {
        return null;
    }

    const retailers = getRetailerNames(input.retailers);

    if (retailers == null) {
        return null;
    }

    const response = getToolResponseById<CommercialSimulatorToolResponse>(
        chatMessages,
        toolRequest.id
    );
    const result = {
        objective: capitalizeFirstLetter(input.objective),
        skuName: capitalizeFirstLetter(
            response?.tool_call_user_selection.sku_name ?? input.sku_name
        ),
        retailers
    };

    return result;
};

const getSubBrandSimulationHeadingData = (
    toolRequest: CommercialSimulatorSubBrandToolRequest
): SimulationHeadingData | null => {
    const input = toolRequest.input;

    if (!input?.objective) {
        return null;
    }

    const retailers = getRetailerNames(input.retailers);

    if (retailers == null) {
        return null;
    }

    return {
        objective: capitalizeFirstLetter(input.objective),
        subBrandName: input.sub_brand_name
            ? `${input.sub_brand_name?.base_name} 
                    (${input.sub_brand_name?.quantity_2} ${getUnitOfMeasureAbbreviation(input.sub_brand_name?.unit_of_measurement_2 ?? 0)} 
                    x ${input.sub_brand_name?.quantity_1}${getUnitOfMeasureAbbreviation(input.sub_brand_name?.unit_of_measurement_1 ?? 0)})`
            : "",
        retailers
    };
};

export const getSimulationHeadingData = (
    chatMessages: ChatMessageDto[] | undefined,
    payload: unknown
): SimulationHeadingData | null => {
    if (chatMessages == null) {
        return null;
    }

    if (isCommercialSimulatorToolRequest(payload)) {
        return getCommercialSimulationHeadingData(chatMessages, payload);
    }

    if (isCommercialSimulatorSubBrandToolRequest(payload)) {
        return getSubBrandSimulationHeadingData(payload);
    }

    return null;
};

const getSimulationPlanData = (payload?: CommercialSimulatorSubBrandToolRequest | CommercialSimulatorToolRequest): SimulationPlanData | null => {
    if (!payload) {
        return null;
    }

    return {
        planningLevel: payload.name.replace("commercial_simulator_", "").split("_").map(x => capitalizeFirstLetter(x)).join(" "),
        budget: payload.input?.plan_budget ?? ""
    };
};

const getScenarioDefaultName = (chatMessages: ChatMessageDto[] | undefined, toolResponse: ToolResponse, payload: unknown): string | null => {
    if (isCommercialSimulatorToolRequest(payload) && isCommercialSimulatorToolResponse(toolResponse)) {
        const userSelection = toolResponse.tool_call_user_selection;
        const count: number = chatMessages?.filter(message => message.type === ChatMessageType.AgentResponse).length ?? 0;
        return `${userSelection.sku_name} - ${userSelection.retailer_name} - ${payload.input?.objective} - ${payload.input?.plan_time_period_info} - v${count}`;
    }

    if (isCommercialSimulatorSubBrandToolRequest(payload)) {
        return null;
    }

    return null;
};

const useCurrentPlan = (): CurrentPlanData => {
    const chatMessages = useAppSelector(selectMessages);
    const currentPlan = useAppSelector(selectCurrentCommercialPlan);

    const toolRequestMessage = getToolRequestMessageById(chatMessages, currentPlan?.tool_use_id);
    const toolResponseMessage = getToolResponseMessageById(chatMessages, currentPlan?.tool_use_id);

    if (!currentPlan || !toolRequestMessage
        || !toolResponseMessage
        || (!isCommercialSimulatorSubBrandToolRequest(toolRequestMessage.payload)
            && !isCommercialSimulatorToolRequest(toolRequestMessage.payload))) {
        return emptyCurrentPlanData;
    }

    const planningData = getSimulationPlanData(toolRequestMessage.payload);
    const scenarioDefaultName = getScenarioDefaultName(chatMessages, toolResponseMessage.payload, toolRequestMessage.payload);

    return {
        scenarioDefaultName,
        hasPlan: Boolean(currentPlan && toolRequestMessage && toolResponseMessage),
        chatConversationId: toolRequestMessage.message.chat_conversation_id ?? null,
        chatConversationMessageId: toolRequestMessage.message.id ?? null,
        headingData: getSimulationHeadingData(
            chatMessages,
            toolRequestMessage.payload
        ),
        currentPlan: currentPlan ?? null,
        currentPlanData: planningData ?? null,
        toolRequest: toolRequestMessage.message ?? null,
        toolResponse: toolResponseMessage.message ?? null
    };
};

export default useCurrentPlan;
